import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import apiService from '../services/api';
import TempStart from './TempStart';
import AppShell, { useShellUser } from '../components/AppShell';
import SquadCover from '../components/squads/SquadCover';
import SquadCodeModal from '../components/squads/SquadCodeModal';
import GameSheet from '../components/games/GameSheet';
import PredictDialog from '../components/games/PredictDialog';
import CheckInNotice from '../components/games/CheckInNotice';
import { ChevronRight, RankBadge } from '../components/games/gameBits';
import { apiErrorMessage, flattenScheduleEvents, formatEventDate, formatEventTime, hasFinalScore, isPredictionOpen, schoolIdOf } from '../utils/gameSchedule';
import { checkInNotice } from '../utils/gameCards';
import { pickGameDayEvents } from '../utils/gameDay';
import { COLLAPSED_SQUAD_ROWS, visibleSquadRows, winsLabel } from '../utils/squadRows';
import '../styles/Games.css';
import '../styles/Squad.css';

const REFRESH_MS = 30000;

function statusLabel(event) {
  if (hasFinalScore(event)) return 'Final';
  return isPredictionOpen(event) ? 'Upcoming' : 'Live';
}

function matchupText(event) {
  const base = `vs. ${event.opponent_name || 'Opponent'}`;
  return hasFinalScore(event) ? `${base} · ${event.home_score} : ${event.away_score}` : base;
}

function whenText(event) {
  const time = formatEventTime(event);
  return time && time !== 'TBD' ? `${formatEventDate(event)} · ${time}` : formatEventDate(event);
}

function BackIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M10 3.5 5.5 8 10 12.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  );
}

function PersonIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="8" cy="5.5" r="3" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M2.5 14a5.5 5.5 0 0 1 11 0" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

// The squad page of the embed: the cover hero with its metrics, Invite and Settings, then Game
// day, the Prediction Leaderboard and the members.
function SquadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, setUser } = useShellUser();
  const [squad, setSquad] = useState(null);
  const [error, setError] = useState('');
  const [events, setEvents] = useState([]);
  const [codeOpen, setCodeOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [sheetEventId, setSheetEventId] = useState(null);
  const [predictEvent, setPredictEvent] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await apiService.getSquad(id);
      setSquad(data);
      setError('');
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load this squad.'));
    }
  }, [id]);

  useEffect(() => {
    load();
    const timer = setInterval(() => {
      if (!document.hidden) load();
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  // Game day: the school's schedule, reduced to the game in progress or just finished and the next.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const current = user || (await apiService.fetchCurrentUser().catch(() => null));
        if (!cancelled && current && current !== user) setUser(current);
        const schoolId = schoolIdOf(current);
        if (!schoolId) return;
        const schedules = await apiService.getSportSchedules(schoolId);
        const bundles = await Promise.all(schedules.map(async (sport) => ({ id: sport.id, events: await apiService.getScheduleEvents(sport.id) })));
        if (cancelled) return;
        const byId = {};
        bundles.forEach((bundle) => {
          byId[bundle.id] = bundle.events;
        });
        setEvents(flattenScheduleEvents(schedules, byId, current));
      } catch (_err) {
        if (!cancelled) setEvents([]);
      }
    })();
    return () => {
      cancelled = true;
    };
    // The schedule is loaded once for the page; the user object only supplies the school.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rememberPrediction = (saved) => {
    if (!saved?.id) return;
    setEvents((prev) => prev.map((row) => (String(row.id) === String(saved.id) ? { ...row, already_predicted: true, my_prediction: saved.my_prediction } : row)));
  };

  const startCheckIn = (event) => {
    setSheetEventId(null);
    const why = checkInNotice(event, Date.now());
    if (why) {
      setNotice({ event, why });
      return;
    }
    navigate('/games');
  };

  if (error) {
    return (
      <div className="squad-page">
        <header className="squad-bar">
          <Link to="/profile" className="squad-back" aria-label="Back"><BackIcon /></Link>
          <h1>Squad</h1>
        </header>
        <p className="squad-error">{error}</p>
      </div>
    );
  }
  if (!squad) return <p className="games-empty">Loading squad...</p>;

  const gameDay = pickGameDayEvents(events);
  const wins = (Array.isArray(squad.prediction_wins) ? squad.prediction_wins : []).filter((row) => Number(row.wins) > 0);
  const members = Array.isArray(squad.members) ? squad.members : [];
  const shownMembers = visibleSquadRows(members, (row) => !!(row.is_you || row.isYou), expanded);
  const sheetEvent = sheetEventId ? events.find((row) => String(row.id) === String(sheetEventId)) : null;

  return (
    <div className="squad-page">
      <SquadCover squad={squad} className="squad-hero">
        <span className="squad-hero-shade" aria-hidden="true" />
        <Link to="/profile" className="squad-back" aria-label="Back"><BackIcon /></Link>
        <div className="squad-hero-tools">
          <button type="button" className="squad-invite" onClick={() => setCodeOpen(true)}>
            <PersonIcon />
            Invite
          </button>
          <Link to={`/squads/${encodeURIComponent(id)}/settings`} className="squad-gear" aria-label="Settings"><GearIcon /></Link>
        </div>
        <div className="squad-hero-text">
          <span className="squad-hero-badge">Squad</span>
          <h1>{squad.name}</h1>
          <div className="squad-metrics">
            <span><strong>{String(squad.member_count ?? 0)}</strong><em>Members</em></span>
            <span><strong>{String(squad.games_attended ?? 0)}</strong><em>Games attended</em></span>
          </div>
        </div>
      </SquadCover>

      {gameDay.length ? (
        <section className="squad-section">
          <h2>Game day</h2>
          <div className="squad-gameday">
            {gameDay.map((event) => (
              <button type="button" key={String(event.id)} className="squad-gameday-card" onClick={() => setSheetEventId(event.id)}>
                <span className={`games-pill is-${statusLabel(event).toLowerCase()}`}>{statusLabel(event)}</span>
                <span className="squad-gameday-copy">
                  <strong>{matchupText(event)}</strong>
                  <em>{whenText(event)}</em>
                </span>
                <ChevronRight />
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {wins.length ? (
        <section className="squad-section">
          <h2>Prediction Leaderboard</h2>
          <ul className="squad-rows">
            {wins.map((row) => (
              <li key={String(row.user_id)} className={`squad-row${row.is_you ? ' is-you' : ''}`}>
                <RankBadge rank={row.rank} />
                <span className="squad-row-name">{row.is_you ? 'You' : row.display_name || 'Fan'}</span>
                <span className="squad-row-detail">{winsLabel(row)}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="squad-section">
        <h2>Members</h2>
        <ul className="squad-rows">
          {shownMembers.map((member) => (
            <li key={String(member.id)} className={`squad-row${member.is_you ? ' is-you' : ''}`}>
              <span className="squad-row-name">{member.is_you ? 'You' : member.display_name || 'Fan'}</span>
              {!member.is_you && member.fan_id ? (
                <Link to={`/hc_prof/${encodeURIComponent(member.fan_id)}`} className="squad-row-profile" aria-label="View profile"><PersonIcon /></Link>
              ) : null}
            </li>
          ))}
        </ul>
        {members.length > COLLAPSED_SQUAD_ROWS ? (
          <button type="button" className="squad-more" onClick={() => setExpanded((open) => !open)}>
            {expanded ? 'Show less' : 'Show more'}
          </button>
        ) : null}
      </section>

      {codeOpen ? <SquadCodeModal code={squad.join_code || ''} onClose={() => setCodeOpen(false)} /> : null}
      {sheetEvent ? (
        <GameSheet
          key={String(sheetEvent.id)}
          event={sheetEvent}
          mode="tabs"
          initialTab="predictions"
          squadId={id}
          switcher={false}
          onClose={() => setSheetEventId(null)}
          onSaved={rememberPrediction}
          onChangePrediction={(event) => {
            setSheetEventId(null);
            if (isPredictionOpen(event)) setPredictEvent(event);
          }}
          onCheckIn={startCheckIn}
        />
      ) : null}
      {predictEvent ? (
        <PredictDialog
          event={events.find((row) => String(row.id) === String(predictEvent.id)) || predictEvent}
          onClose={() => setPredictEvent(null)}
          onSaved={rememberPrediction}
        />
      ) : null}
      {notice ? <CheckInNotice event={notice.event} notice={notice.why} onClose={() => setNotice(null)} /> : null}
    </div>
  );
}

function SquadDetailGate() {
  if (!apiService.isAuthenticated()) return <TempStart />;
  return (
    <AppShell title="Squad">
      <SquadDetailPage />
    </AppShell>
  );
}

export default SquadDetailGate;
